import axios from "axios";
import {
  ExtractGroup,
  CreateExtractJobPayload,
  CreateExtractJobResponse,
  ExtractJobDTO,
  ExtractJobListResponse,
} from "../types/extract.types";

const BACKEND_URL = process.env.REACT_APP_BACKEND_API_URL + "/api/v1";

/**
 * Thrown by createExtractJob when the backend returns 409 because the
 * account already has a pending or running extract. Lets the UI redirect
 * the user to the existing job rather than show a generic error.
 */
export class ExtractJobConflictError extends Error {
  existingJobId?: string;

  constructor(message: string, existingJobId?: string) {
    super(message);
    this.name = "ExtractJobConflictError";
    this.existingJobId = existingJobId;
  }
}

/** Thrown when the file is past its 30-day retention. UI surfaces a
 *  distinct message ("expired") rather than the generic download error. */
export class ExtractJobExpiredError extends Error {
  constructor(message = "Cet extract a expiré (rétention 30 jours dépassée).") {
    super(message);
    this.name = "ExtractJobExpiredError";
  }
}

export const getExtractGroups = async (): Promise<ExtractGroup[]> => {
  return (await axios.get<ExtractGroup[]>(`${BACKEND_URL}/extract/groups`)).data;
};

export const createExtractJob = async (
  payload: CreateExtractJobPayload
): Promise<CreateExtractJobResponse> => {
  try {
    const res = await axios.post<CreateExtractJobResponse>(
      `${BACKEND_URL}/extract/jobs`,
      payload
    );
    return res.data;
  } catch (err: any) {
    if (err?.response?.status === 409) {
      const data = err.response.data || {};
      throw new ExtractJobConflictError(
        data.message || "Une extraction est déjà en cours pour ce compte.",
        data.existingJobId
      );
    }
    throw err;
  }
};

export const listExtractJobs = async (): Promise<ExtractJobListResponse> => {
  const res = await axios.get<ExtractJobListResponse>(`${BACKEND_URL}/extract/jobs`);
  return res.data;
};

export const getExtractJob = async (id: string): Promise<ExtractJobDTO> => {
  const res = await axios.get<ExtractJobDTO>(`${BACKEND_URL}/extract/jobs/${id}`);
  return res.data;
};

// Use axios so the download path goes through the same interceptor stack
// as the rest of the app — auth refresh, base URL handling, and error
// shape stay consistent. The response is requested as a Blob so the
// browser's File Save dialog gets a proper binary stream.
export const downloadExtractJob = async (id: string): Promise<Blob> => {
  try {
    const res = await axios.get<Blob>(
      `${BACKEND_URL}/extract/jobs/${id}/download`,
      { responseType: "blob" }
    );
    return res.data;
  } catch (err: any) {
    if (err?.response?.status === 410) {
      throw new ExtractJobExpiredError();
    }
    throw err;
  }
};

/**
 * Triggers a browser-side download of `blob` as `filename`. Wrapped here so
 * components don't have to recreate the object-URL boilerplate every time.
 * Must be invoked synchronously inside a user gesture handler.
 */
export const triggerBrowserDownload = (blob: Blob, filename: string): void => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

export const extractAPI = {
  getExtractGroups,
  createExtractJob,
  listExtractJobs,
  getExtractJob,
  downloadExtractJob,
  triggerBrowserDownload,
};

export default extractAPI;
